import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getToken, logout as authLogout, fetchUserInfo as fetchUserInfoFromAuth } from '../utils/auth';
import Star612Login from './Star612Login';
import './Header.css';

// 로고 경로 처리 함수
const getLogoPath = (filename) => {
  const publicUrl = process.env.PUBLIC_URL || '';
  const basePath = window.location.pathname.startsWith('/trading') ? '/trading' : '';
  return `${basePath || publicUrl}/images/${filename}`;
};

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [user, setUser] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const [showStar612Login, setShowStar612Login] = useState(false);
  const [star612LoginTab, setStar612LoginTab] = useState('login'); // 'login' or 'register'
  const [userType, setUserType] = useState(null); // 'trading' or 'star612'
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // 초기 로드 시 스크롤 위치 확인
    const initialScroll = window.scrollY;
    if (initialScroll === 0) {
      // 스크롤이 없어도 헤더가 보이도록 설정
      setIsScrolled(false);
    } else {
      setIsScrolled(initialScroll > 50);
    }

    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // 라우트가 변경될 때마다 메뉴를 닫습니다.
    setIsMenuOpen(false);
    
    // Trading 페이지에서는 항상 scrolled 상태로 설정
    if (location.pathname === '/trading') {
      setIsScrolled(true);
    } else {
      // 다른 페이지에서는 스크롤 위치에 따라 결정
      setIsScrolled(window.scrollY > 50);
    }

    // 사용자 정보 조회 (Trading 페이지가 아닐 때만)
    if (location.pathname !== '/trading') {
      // 사용자 타입 확인
      const storedUserType = localStorage.getItem('user_type');
      setUserType(storedUserType);
      
      if (storedUserType === 'star612') {
        // star612.net 일반 사용자
        const star612Token = localStorage.getItem('star612_token');
        if (star612Token) {
          fetchStar612UserInfo(star612Token);
        } else {
          setUser(null);
        }
      } else {
        // trading-service 사용자
        const token = getToken();
        if (token) {
          fetchUserInfo();
        } else {
          setUser(null);
        }
      }
    }
  }, [location]); // eslint-disable-line react-hooks/exhaustive-deps

  // 사용자 정보 조회 (auth.js의 캐싱된 fetchUserInfo 사용)
  const fetchUserInfo = async () => {
    try {
      const token = getToken();
      if (!token) {
        setUser(null);
        setIsLoadingUser(false);
        return;
      }

      setIsLoadingUser(true);
      const userData = await fetchUserInfoFromAuth(false); // 캐시 사용
      if (userData) {
        setUser({ ...userData, userType: 'trading' });
        setUserType('trading');
      } else {
        setUser(null);
        // 401 에러는 fetchUserInfoFromAuth에서 이미 처리됨
      }
    } catch (error) {
      console.error('사용자 정보 조회 실패:', error);
      // Rate limit 에러는 캐시된 데이터가 있으면 사용 (fetchUserInfoFromAuth가 처리)
      // 다른 에러는 사용자 정보를 null로 설정
      if (!error.message || !error.message.includes('Rate limit')) {
        setUser(null);
      }
    } finally {
      setIsLoadingUser(false);
    }
  };

  // star612.net 일반 사용자 정보 조회
  const fetchStar612UserInfo = async (token) => {
    try {
      setIsLoadingUser(true);
      const apiUrl = window.location.origin.includes('localhost') 
        ? 'http://localhost:5000/api/auth/star612/me'
        : '/api/auth/star612/me';
      
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUser({ ...data.user, userType: 'star612' });
        setUserType('star612');
      } else {
        setUser(null);
        localStorage.removeItem('star612_token');
        localStorage.removeItem('user_type');
      }
    } catch (error) {
      console.error('star612.net 사용자 정보 조회 실패:', error);
      setUser(null);
    } finally {
      setIsLoadingUser(false);
    }
  };

  // star612.net 로그인 성공 핸들러
  const handleStar612LoginSuccess = (userData, token) => {
    setUser({ ...userData, userType: 'star612' });
    setUserType('star612');
    setShowStar612Login(false);
  };

  // 로그아웃 핸들러
  const handleLogout = () => {
    if (userType === 'star612') {
      localStorage.removeItem('star612_token');
      localStorage.removeItem('user_type');
    } else {
      authLogout();
    }
    setUser(null);
    setUserType(null);
    setShowUserMenu(false);
    navigate('/');
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Trading 페이지에서는 항상 scrolled 스타일 적용
  const isTradingPage = location.pathname === '/trading';
  const headerClassName = `header ${isScrolled || isTradingPage ? 'scrolled' : ''} ${isTradingPage ? 'trading-page' : ''}`;

  // Trading 페이지에서 헤더가 항상 보이도록 인라인 스타일 추가
  const headerStyle = isTradingPage ? {
    display: 'block',
    visibility: 'visible',
    opacity: 1,
    zIndex: 9999,
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    width: '100%',
    backgroundColor: '#fff',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  } : {};

  // 사용자 메뉴 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserMenu && !event.target.closest('.header-user-info')) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showUserMenu]);

  // Trading 페이지에서는 헤더 숨김
  if (isTradingPage) {
    return null;
  }

  return (
    <header 
      className={headerClassName} 
      style={{
        ...headerStyle,
        display: 'block',
        visibility: 'visible',
        opacity: 1,
        zIndex: 9999
      }}
    >
      <div className="header-content">
        <Link to="/">
          <img 
            src={isScrolled ? getLogoPath('logo-612c.png') : getLogoPath('logo-612w.png')} 
            alt="612 로고" 
            className="logo" 
          />
        </Link>
        <nav className={`nav ${isMenuOpen ? 'open' : ''}`}>
          <ul>
            <li><Link to="/vision">비전</Link></li>
            <li><Link to="/team">팀소개</Link></li>
            <li><Link to="/product">서비스</Link></li>
            <li><Link to="/contact">CONTACT</Link></li>
          </ul>
        </nav>
        <div className={`auth-buttons ${isMenuOpen ? 'open' : ''}`}>
          {isLoadingUser ? (
            <div style={{ 
              color: isScrolled || isTradingPage ? '#333' : '#fff',
              padding: '8px 12px',
              fontSize: '14px'
            }}>
              로딩 중...
            </div>
          ) : user ? (
            <div className="header-user-info" style={{ position: 'relative' }}>
              <button 
                className="user-menu-button"
                onClick={() => setShowUserMenu(!showUserMenu)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: isScrolled || isTradingPage ? '#333' : '#fff',
                  cursor: 'pointer',
                  padding: '8px 12px',
                  borderRadius: '5px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                <span>👤 {user.nickname || user.name || '사용자'}</span>
                <span style={{ fontSize: '10px' }}>▼</span>
              </button>
              {showUserMenu && (
                <div 
                  className="user-menu-dropdown"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '5px',
                    background: '#fff',
                    border: '1px solid #ddd',
                    borderRadius: '5px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    minWidth: '150px',
                    zIndex: 10000
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div style={{ padding: '10px', borderBottom: '1px solid #eee', fontSize: '12px', color: '#666' }}>
                    {user.email}
                  </div>
                  {user.userType === 'trading' && (
                    <button
                      onClick={() => {
                        navigate('/trading');
                        setShowUserMenu(false);
                      }}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: 'none',
                        background: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      📈 거래하기
                    </button>
                  )}
                  <button
                    onClick={() => {
                      authLogout();
                      setUser(null);
                      setShowUserMenu(false);
                      navigate('/');
                    }}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: 'none',
                      background: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: '#ff6b6b'
                    }}
                  >
                    로그아웃
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <button 
                onClick={() => {
                  setStar612LoginTab('login');
                  setShowStar612Login(true);
                }}
                style={{
                  background: 'transparent',
                  border: `1px solid ${isScrolled || isTradingPage ? '#333' : '#fff'}`,
                  color: isScrolled || isTradingPage ? '#333' : '#fff',
                  padding: '8px 16px',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  marginRight: '10px',
                  transition: 'all 0.3s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = isScrolled || isTradingPage ? '#333' : '#fff';
                  e.target.style.color = isScrolled || isTradingPage ? '#fff' : '#333';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = isScrolled || isTradingPage ? '#333' : '#fff';
                }}
              >
                로그인
              </button>
              <button 
                onClick={() => {
                  setStar612LoginTab('register');
                  setShowStar612Login(true);
                }}
                style={{
                  background: 'transparent',
                  border: `1px solid ${isScrolled || isTradingPage ? '#333' : '#fff'}`,
                  color: isScrolled || isTradingPage ? '#333' : '#fff',
                  padding: '8px 16px',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  transition: 'all 0.3s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = isScrolled || isTradingPage ? '#333' : '#fff';
                  e.target.style.color = isScrolled || isTradingPage ? '#fff' : '#333';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = isScrolled || isTradingPage ? '#333' : '#fff';
                }}
              >
                회원가입
              </button>
            </>
          )}
        </div>
        <button className={`hamburger ${isScrolled ? 'scrolled' : ''}`} onClick={toggleMenu}>
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
      {showStar612Login && (
        <Star612Login
          onLoginSuccess={handleStar612LoginSuccess}
          onClose={() => setShowStar612Login(false)}
          initialTab={star612LoginTab}
        />
      )}
    </header>
  );
}

export default Header;